import type { Sort } from 'mongodb'
// import { type Feature } from 'geojson'
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from 'react'
import { ValidatedForm, useControlField } from 'remix-validated-form'
import {
  useAdminCompaniesQuery,
  useGetListingParcels,
  useListingsQuery,
} from '~/api'
import {
  aOrganization,
  aUser,
  getSortFromFilters,
  useAtomValue,
  type MapFiltersType,
} from '~/atoms'
import { FieldContainer, LoadingCircle } from '~/components'
import PdfClientDownload from '~/components/pdfs/PdfClientDownload.client'
import { Button, Dialog, Input } from '~/components/ui'
import { useDebounce } from '~/hooks'
import {
  ReportPdfType,
  getDefaultValues,
  getFindFromReport,
  normalizeReportPdfOptions,
  reportPdfValidator,
  type Listing,
} from '~/models'
import PdfFormContent from './FormContent'
const CreatePdfPreview = lazy(() => import('./CreatePdfPreview'))

function PdfTitle({
  formId,
  initialTitle,
}: {
  formId: string
  initialTitle: string
}) {
  const [titleValue, setTitleValue] = useState<string>(initialTitle)
  const debouncedTitleValue = useDebounce(titleValue, 1000)
  const [_, setTitle] = useControlField<string>('title', formId)

  useEffect(() => {
    setTitle(debouncedTitleValue)
  }, [debouncedTitleValue, setTitle])

  return (
    <FieldContainer label="Title" id="title" className="col-span-12">
      <Input
        id="title"
        name="title"
        value={titleValue}
        onChange={(e) => setTitleValue(e.target.value.replace('.', ''))}
      />
    </FieldContainer>
  )
}

export type CreatePdfDialogContentProps = {
  listingIds: string[]
  filters?: MapFiltersType
  initialTitle?: string
  singleListing?: boolean
}

function CreatePdfDialogContent({
  listingIds,
  filters,
  initialTitle = '',
  singleListing = false,
}: CreatePdfDialogContentProps) {
  const organization = useAtomValue(aOrganization)
  const user = useAtomValue(aUser)

  const [isLoaded, setIsLoaded] = useState(false)
  const [isPreviewLoading, setIsPreviewLoading] = useState(true)
  const [previewDocument, setPreviewDocument] = useState<ReactElement | null>(
    null
  )
  useEffect(() => {
    setIsLoaded(true)
  }, [])

  const { companies } = useAdminCompaniesQuery(
    ['pdf-user-company', 'disable-cache'],
    { _id: user?.company?._id },
    { enabled: !!user?.company }
  )
  const company = useMemo(() => {
    if (user && companies.length > 0) {
      return companies[0]
    }
  }, [companies, user])

  const find = useMemo(() => {
    return getFindFromReport({
      includeIds: listingIds,
      // filters: locationState.filters,
      // location: getStateLocation,
    })
  }, [listingIds])

  const sort: Sort | undefined = useMemo(() => {
    return filters ? getSortFromFilters(filters) : {}
  }, [filters])

  const { listings, isLoading } = useListingsQuery({
    key: ['create-pdf-listings', 'disable-cache', ...listingIds],
    find,
    sort,
    limit: 60,
    refetchOnMount: false,
    enabled: true,
  })

  const sortedListings = useMemo(() => {
    if (listingIds) {
      return (
        (listingIds
          .filter((_id) => listings.find((listing) => listing._id === _id))
          .map((_id) => {
            return listings.find((listing) => listing._id === _id)
          })
          .filter((v) => v) as Listing[]) || []
      )
    }
    return listings
  }, [listingIds, listings])

  const formId = 'create-pdf'
  const [title] = useControlField<string>('title', formId)
  const [type] = useControlField<ReportPdfType>('options.type', formId)
  const [preparedFor] = useControlField<string>('options.preparedFor', formId)
  const [notes] = useControlField<string>('options.notes', formId)
  const [subjectListingId] = useControlField<string | undefined>(
    'options.subjectListingId',
    formId
  )
  const [showTitlePage] = useControlField<boolean>(
    'options.showTitlePage',
    formId
  )
  const [showContents] = useControlField<boolean>(
    'options.showContents',
    formId
  )
  const [showBrokerProfile] = useControlField<boolean>(
    'options.showBrokerProfile',
    formId
  )
  const [showQrCodes] = useControlField<boolean>('options.showQrCodes', formId)
  const [showMapPage] = useControlField<boolean>('options.showMapPage', formId)
  const [mapShowListingSidebar] = useControlField<boolean>(
    'options.mapShowListingSidebar',
    formId
  )
  const [mapType] = useControlField<'streets' | 'satellite'>(
    'options.mapType',
    formId
  )
  const [showLeaseSpaces] = useControlField<boolean>(
    'options.showLeaseSpaces',
    formId
  )
  const [showLeaseTransactions] = useControlField<boolean>(
    'options.showLeaseTransactions',
    formId
  )
  const [showBrokers] = useControlField<boolean>('options.showBrokers', formId)
  const [showImages] = useControlField<boolean>('options.showImages', formId)
  const [showParcels] = useControlField<boolean>('options.showParcels', formId)
  const resolvedMapShowListingSidebar = mapShowListingSidebar ?? true
  const resolvedMapType = mapType ?? 'streets'

  const { parcels } = useGetListingParcels({
    enabled: showParcels,
    listings,
  })

  const pdfOptions = useMemo(
    () => ({
      ...normalizeReportPdfOptions({
        type,
        preparedFor,
        notes,
        subjectListingId,
        showTitlePage,
        showContents,
        showBrokerProfile,
        showQrCodes,
        showMapPage,
        mapShowListingSidebar: resolvedMapShowListingSidebar,
        mapType: resolvedMapType,
        showLeaseSpaces,
        showLeaseTransactions,
        showBrokers,
        showImages,
        showParcels,
      }),
      env: {
        GOOGLE_MAPS_API_KEY:
          typeof window === 'undefined'
            ? ''
            : (window.ENV?.GOOGLE_MAPS_API_KEY ?? ''),
        REPORT_MAP_URL: import.meta.env.VITE_REPORT_MAP_URL || '',
      },
    }),
    [
      type,
      preparedFor,
      notes,
      subjectListingId,
      showTitlePage,
      showContents,
      showBrokerProfile,
      showQrCodes,
      showMapPage,
      resolvedMapShowListingSidebar,
      resolvedMapType,
      showLeaseSpaces,
      showLeaseTransactions,
      showBrokers,
      showImages,
      showParcels,
    ]
  )

  const onPreviewDocumentChange = useCallback(
    (document: ReactElement | null) => setPreviewDocument(document),
    []
  )

  const defaultValues = useMemo(() => {
    return getDefaultValues(null, {
      title: initialTitle.replace(/\./g, ''),
      options: normalizeReportPdfOptions({
        type: ReportPdfType.flyers,
        showTitlePage: false,
        // showContents: sortedListings.length > 20 ? false : true,
        showContents: false,
        showBrokerProfile: true,
        // showBrokerProfile:
        //   user?.role === UserRole.broker ||
        //   user?.role === UserRole.managingBroker
        //     ? true
        //     : false,
        // showMapPage: sortedListings.length > 20 ? false : true,
        mapShowListingSidebar: true,
        mapType: 'streets',
        showQrCodes: true,
        showMapPage: false,
        showLeaseSpaces: false,
        showLeaseTransactions: false,
        showBrokers: false,
        showImages: false,
        showParcels: false,
      }),
    })
  }, [initialTitle])

  if (!user) {
    return null
  }

  return (
    <Dialog.Content className="flex h-full max-w-none flex-col sm:h-[calc(100vh-8rem)] sm:w-[calc(100vw-8rem)]">
      <Dialog.Header>
        <Dialog.Title className="">Download PDF</Dialog.Title>
        <Dialog.Description className="sr-only">
          Download PDF
        </Dialog.Description>
      </Dialog.Header>

      <div className="grid flex-1 grid-cols-12 gap-8 overflow-x-auto overflow-y-auto">
        <div className="col-span-12 px-1 lg:col-span-4 lg:overflow-auto">
          <ValidatedForm
            id={formId}
            validator={reportPdfValidator}
            defaultValues={defaultValues as any}
            // method="post"
            // action={`/admin/reports/${report.slug}/pdf`}
          >
            <div className="grid grid-cols-12 gap-6 pt-4">
              <PdfTitle
                formId={formId}
                initialTitle={initialTitle.replace(/\./g, '')}
              />
              <p className="col-span-12 -mb-4 text-lg font-medium">
                PDF Options
              </p>
              <PdfFormContent
                formId={formId}
                singleListing={singleListing}
                listings={listings}
              />
            </div>
          </ValidatedForm>
        </div>
        <div className="relative col-span-12 h-[500px] lg:col-span-8 lg:h-auto">
          {isPreviewLoading && (
            <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center bg-muted/50 pb-64">
              <LoadingCircle className="h-10 w-10 text-primary" />
              <p className="mt-2">Loading...</p>
            </div>
          )}
          {isLoaded && !isLoading && organization && user && (
            <Suspense fallback={null}>
              <CreatePdfPreview
                organization={organization}
                filters={filters}
                title={title}
                company={company}
                user={user}
                listings={sortedListings}
                parcels={parcels}
                pdfOptions={pdfOptions}
                onDocumentChange={onPreviewDocumentChange}
                onLoadingChange={setIsPreviewLoading}
              />
            </Suspense>
          )}
        </div>
      </div>

      <Dialog.Footer className="pb-3">
        <Dialog.Close asChild>
          <Button variant="ghost">Close</Button>
        </Dialog.Close>
        {previewDocument && (
          <PdfClientDownload fileName={title} document={previewDocument}>
            Download
          </PdfClientDownload>
        )}
      </Dialog.Footer>
    </Dialog.Content>
  )
}

function areFiltersEqual(
  left?: MapFiltersType,
  right?: MapFiltersType
): boolean {
  if (left === right) {
    return true
  }
  if (!left || !right) {
    return false
  }
  return JSON.stringify(left) === JSON.stringify(right)
}

function areDialogPropsEqual(
  prev: Readonly<CreatePdfDialogContentProps>,
  next: Readonly<CreatePdfDialogContentProps>
) {
  if (prev.initialTitle !== next.initialTitle) {
    return false
  }
  if (prev.singleListing !== next.singleListing) {
    return false
  }
  if (!areFiltersEqual(prev.filters, next.filters)) {
    return false
  }
  if (prev.listingIds.length !== next.listingIds.length) {
    return false
  }
  for (let i = 0; i < prev.listingIds.length; i++) {
    if (prev.listingIds[i] !== next.listingIds[i]) {
      return false
    }
  }
  return true
}

export default memo(CreatePdfDialogContent, areDialogPropsEqual)
